/**
 * What the customer submitted, with anything we entered for them marked as ours.
 *
 * Read-only. The token is deliberately absent and could not be shown anyway:
 * only its sha256 is stored, so a leak of this page cannot open anybody's form.
 */
import page from '../../../../shared/Page.module.css';
import { ProvenanceNote } from '../ProvenanceNote';
import { StaffMark, staffWords } from '../StaffMark';
import { organizationTypeLabel } from '../../../intake/organizationTypes';
import type { Onboarding } from '../types';
import { GroupCard } from '../../../../shared/GroupCard';

const when = (iso: string | null) => (iso
  ? new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
  : null);

const yesNo = (v: boolean | null) => (v === true ? 'Yes' : v === false ? 'No' : 'Not answered');

const SECTION_LABEL: Record<string, string> = {
  submitter: 'About them', organization: 'Their organization', champion: 'Champion',
  signatory: 'Signatory', billing: 'Billing contact', systemAdmin: 'System Admin',
  requirements: 'Their process',
};

export function IntakeView({ data }: { data: Onboarding }) {
  const i = data.intake;
  const staff = i?.staffFields ?? {};

  if (!i) {
    return (
      <p className={page.absent}>
        No onboarding form has been opened for this customer. The Organization stage sends one,
        and its Edit answers lets you enter their answers yourself.
      </p>
    );
  }

  return (
    <>
      <GroupCard title="The form">
      <dl className={page.facts}>
        <div><dt className={page.factTerm}>Submitted</dt>
          <dd className={page.factValue}>{when(i.submittedAt) ?? <span className={page.absent}>Not submitted yet</span>}</dd></div>
        <div><dt className={page.factTerm}>Last saved</dt>
          <dd className={page.factValue}>{when(i.updatedAt) ?? '—'}</dd></div>
        <div><dt className={page.factTerm}>Filled in by</dt>
          <dd className={page.factValue}>
            {i.submitter.name
              ? `${i.submitter.name}${i.submitter.email ? ` · ${i.submitter.email}` : ''}`
              // Never guessed from an address. A form started before the
              // submitter section existed simply does not know.
              : <span className={page.absent}>Not recorded</span>}
          </dd></div>
        <div><dt className={page.factTerm}>Link</dt>
          <dd className={page.factValue}>{data.intakeLinkSent ? 'Live' : 'None live'}</dd></div>
      </dl>

      {i.outstanding.length > 0 && (
        <div className={`${page.notice} ${page.noticeWarn}`} style={{ marginTop: 'var(--sp-4)' }}>
          <strong>They have not finished it.</strong> Still to fill in:{' '}
          {i.outstanding.map((s) => SECTION_LABEL[s] ?? s).join(', ')}.
        </div>
      )}

      </GroupCard>

      <GroupCard title="What they told us">
      <dl className={page.facts}>
        <div><dt className={page.factTerm}>Legal name</dt><dd className={page.factValue}>{i.legalName ?? '—'}<StaffMark field={staff['organization.legalName']} /></dd></div>
        <div><dt className={page.factTerm}>Kind of organization</dt>
          <dd className={page.factValue}>{organizationTypeLabel(i.organizationType) ?? '—'}<StaffMark field={staff['organization.organizationType']} /></dd></div>
        <div><dt className={page.factTerm}>Address</dt><dd className={page.factValue}>{i.address ?? '—'}<StaffMark field={staff['organization.address']} /></dd></div>
        <div><dt className={page.factTerm}>Locations</dt><dd className={page.factValue}>{i.locationCount ?? '—'}<StaffMark field={staff['organization.locationCount']} /></dd></div>
        <div><dt className={page.factTerm}>Target go-live</dt><dd className={page.factValue}>{i.targetGoLiveDate ?? '—'}<StaffMark field={staff['organization.targetGoLiveDate']} /></dd></div>
      </dl>

      </GroupCard>

      <GroupCard title="What their process needs">
      <p className={page.itemMeta}>
        These answers decide what we have to send. They are why a business associate agreement
        blocks go-live for one customer and not for another.
      </p>
      <dl className={page.facts}>
        <div><dt className={page.factTerm}>Business associate agreement</dt>
          <dd className={page.factValue}>{yesNo(i.needsBaa)}<StaffMark field={staff['requirements.needsBaa']} /></dd></div>
        <div><dt className={page.factTerm}>Security review</dt>
          <dd className={page.factValue}>{yesNo(i.needsSecurityReview)}<StaffMark field={staff['requirements.needsSecurityReview']} /></dd></div>
        <div><dt className={page.factTerm}>Purchase order needed</dt>
          <dd className={page.factValue}>{yesNo(i.poRequired)}</dd></div>
        <div><dt className={page.factTerm}>Purchase order number</dt>
          <dd className={page.factValue}>{i.poNumber ?? '—'}<StaffMark field={staff['requirements.poNumber']} /></dd></div>
      </dl>

      {i.submittedAt && (i.returnedBy
        ? <div className={page.itemMeta}>Form returned for them · {staffWords(i.returnedBy)}</div>
        : <ProvenanceNote who={i.submitter.name} when={i.submittedAt} />)}
      </GroupCard>
    </>
  );
}
